// src/api/features/payment-links/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createPaymentLink,
  resendPaymentLink,
  cancelPaymentLink,
  refreshPaymentLink,
  refundPaymentLink,
} from "./api";
import { PaymentLinksKeys } from "./types";
import type { PaymentLink, PaymentLinkDTO } from "./types";

/** Create a new payment link. */
export function useCreatePaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PaymentLinkDTO.Create) => createPaymentLink(payload),
    onSuccess: (created) => {
      qc.setQueryData(PaymentLinksKeys.detail(created.id), created);
      qc.invalidateQueries({ queryKey: PaymentLinksKeys.all });
    },
  });
}

/** Resend the link by email. */
export function useResendPaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; customerEmail?: string | null }) =>
      resendPaymentLink(vars.id, vars.customerEmail),
    onSuccess: (updated) => {
      qc.setQueryData(PaymentLinksKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: PaymentLinksKeys.all });
    },
  });
}

/** Cancel a link so it can no longer be paid. */
export function useCancelPaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelPaymentLink(id),
    onSuccess: (updated) => {
      qc.setQueryData(PaymentLinksKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: PaymentLinksKeys.all });
    },
  });
}

/** Re-verify status with HDFC and reconcile. */
export function useRefreshPaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => refreshPaymentLink(id),
    onSuccess: (updated: PaymentLink) => {
      qc.setQueryData(PaymentLinksKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: PaymentLinksKeys.all });
    },
  });
}

/** Refund a paid link (full or partial). */
export function useRefundPaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; payload: PaymentLinkDTO.Refund }) =>
      refundPaymentLink(vars.id, vars.payload),
    onSuccess: (updated) => {
      qc.setQueryData(PaymentLinksKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: PaymentLinksKeys.all });
    },
  });
}
